import React, { useState, useEffect } from 'react'
import { arrayOf, shape, func, string, bool } from 'prop-types'
import Slider from 'react-slick'
import { createComponent } from '@ycos/fela'
import { BackgroundPicture } from '@ycos/picture'
import CampaignVideo from './CampaignVideo'
import useScreenBreakpoint from './utils/src/index'
// Fix 1px line on the right side for the mobile version on Safari
const SlideWrapper = createComponent('SlideWrapper', () => ({
  width: '100%',

  '>.slick-slider .slick-track:before': {
    display: 'none'
  }
}))

const ImageContainer = createComponent('ImageContainer', () => ({
  position: 'relative'
}))

function ImageBlock(props) {
  const {
    pictures,
    config,
    target,
    onTargetClick,
    lazy,
    ynapParameter,
    fetchPriority,
    theme,
    allowMute,
    allowMid,
    allowCampaignBanner,
    infoHeight,
    allowSkinny,
    allowSkinnySale
  } = props
  const { breakpoints } = theme
  const [headerHeight, setHeaderHeight] = useState(0)
  useEffect(() => {
    const header = document.getElementById('header')
    if (header) {
      setHeaderHeight(header.getBoundingClientRect().height)
    }
  }, [])

  const breakpointsConfig = [
    {
      breakpointMinValue: 0,
      breakpointMaxValue: breakpoints.medium.width,
      value: 'mobile'
    },
    {
      breakpointMinValue: breakpoints.medium.width,
      breakpointMaxValue: breakpoints.large.width,
      value: 'tablet'
    }
  ]
  const screenType = useScreenBreakpoint(breakpointsConfig)

  let heightValue
  let overlayTop
  let overlayHeight
  // To calculate overlayHeight as infoHeight plus a bottom padding of 56px
  // Constants
  const CAMPAIGN_MOBILE_HEIGHT = '281px'
  const CAMPAIGN_DESKTOP_HEIGHT = '512px'
  const SKINNY_MOBILE_HEIGHT = '281px'
  const SKINNY_DESKTOP_HEIGHT = '270px'
  const MID_MOBILE_HEIGHT = '496px'
  const MID_DESKTOP_HEIGHT = '540px'
  const MOBILE_OVERLAY_BOTTOM = 48
  const DESKTOP_OVERLAY_BOTTOM = 56
  const DESKTOP_OVERLAY_TOP = 150
  const MOBILE_OVERLAY_TOP = 100
  const DESKTOP_OVERLAY_EXTRA_HEIGHT = DESKTOP_OVERLAY_BOTTOM + DESKTOP_OVERLAY_TOP
  const MOBILE_OVERLAY_EXTRA_HEIGHT = MOBILE_OVERLAY_BOTTOM + MOBILE_OVERLAY_TOP

  switch (true) {
  case allowCampaignBanner:
    heightValue = screenType === 'mobile' ? CAMPAIGN_MOBILE_HEIGHT : CAMPAIGN_DESKTOP_HEIGHT
    overlayHeight = infoHeight + (screenType === 'mobile' ? MOBILE_OVERLAY_EXTRA_HEIGHT - 12 : DESKTOP_OVERLAY_EXTRA_HEIGHT)
    overlayTop = `calc(${heightValue} - ${overlayHeight}px)`
    break
  case allowMid:
    heightValue = screenType === 'mobile' ? MID_MOBILE_HEIGHT : MID_DESKTOP_HEIGHT
    overlayHeight = infoHeight + (screenType === 'mobile' ? MOBILE_OVERLAY_EXTRA_HEIGHT : DESKTOP_OVERLAY_EXTRA_HEIGHT)
    overlayTop = `calc(${heightValue} - ${overlayHeight}px)`
    break
  case allowSkinny:
  case allowSkinnySale:
    heightValue = screenType === 'mobile' ? SKINNY_MOBILE_HEIGHT : SKINNY_DESKTOP_HEIGHT
    overlayHeight = heightValue
    overlayTop = '0'
    break
  default:
    heightValue = `calc(max(100vh - ${headerHeight}px, ${screenType === 'mobile' ? MID_MOBILE_HEIGHT : MID_DESKTOP_HEIGHT}))`
    overlayHeight = infoHeight + (screenType === 'mobile' ? MOBILE_OVERLAY_EXTRA_HEIGHT : DESKTOP_OVERLAY_EXTRA_HEIGHT)
    overlayTop = `calc(max(100vh - ${headerHeight}px, ${screenType === 'mobile' ? MID_MOBILE_HEIGHT : MID_DESKTOP_HEIGHT}) - ${overlayHeight}px)`
  }

  const GradientOverlay = createComponent('GradientOverlay', ({ allowSkinny, allowSkinnySale }) => {
    const gradientForSkinny = allowSkinny || allowSkinnySale ? '270deg' : '180deg'
    const isRtl = theme.direction === 'rtl'
    return {
      position: 'absolute',
      top: overlayTop,
      left: '0',
      width: '100%',
      height: overlayHeight,
      background: 'linear-gradient(180deg, rgba(131, 131, 131, 0.00) 0%, rgba(0, 0, 0, 0.50) 67.71%)',
      pointerEvents: 'none',
      zIndex: '0',
      'screen-medium': {
        width: allowSkinny || allowSkinnySale ? '50%' : '100%',
        background:
          isRtl && (allowSkinny || allowSkinnySale)
            ? 'linear-gradient(90deg, rgba(131, 131, 131, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)'
            : `linear-gradient(${gradientForSkinny}, rgba(131, 131, 131, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)`
      }
    }
  })

  const desktopImages = []
  const mobileImages = []
  pictures.forEach((imageEntry) => {
    const imageUrl = imageEntry.imageUrl
    const height = imageEntry.height
    const width = imageEntry.width
    const type = imageEntry.type
    const mimeType = imageEntry.mimeType
    const aspectRatio = imageEntry.width / imageEntry.height
    if (imageUrl.includes('desktop')) {
      return desktopImages.push({ imageUrl, height, width, type, mimeType })
    }
    if (imageUrl.includes('mobile')) {
      return mobileImages.push({ imageUrl, height, width, type, mimeType })
    }
    if (aspectRatio >= 1) {
      return desktopImages.push({ imageUrl, height, width, type, mimeType })
    }
    return mobileImages.push({ imageUrl, height, width, type, mimeType })
  })

  const imagesToRender = screenType === 'mobile' ? mobileImages : desktopImages
  const settings = {
    arrows: false,
    dots: false,
    fade: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    easing: 'linear'
  }

  const { account, player } = config && config?.brightcove
  const playerId = player?.soundless

  let videoId
  try {
    const { mobileVideoId, desktopVideoId } = JSON.parse(ynapParameter)
    if (screenType === 'mobile') {
      videoId = mobileVideoId
    } else {
      videoId = desktopVideoId
    }
  } catch (err) {
    videoId = undefined
  }

  return (
    <ImageContainer>
      {videoId ? (
        <CampaignVideo
          account={account}
          playerId={playerId}
          videoId={videoId}
          pictures={[imagesToRender[0]]}
          imagesphere={config.imageSphere}
          active
          lazy={lazy}
          target={target}
          allowMute={allowMute}
          onTargetClick={onTargetClick}
          heightValue={heightValue}
        />
      ) : (
        <SlideWrapper>
          <Slider {...settings}>
            {imagesToRender.map((image, index) => (
              <a key={index} href={target.href} onClick={onTargetClick}>
                <BackgroundPicture
                  widths={[1500, 3000]}
                  fetchPriority={fetchPriority}
                  images={[image]}
                  imagesphere={config.imageSphere}
                  width="100vw"
                  height={heightValue}
                  lazy={lazy}
                />
              </a>
            ))}
          </Slider>
        </SlideWrapper>
      )}
      <GradientOverlay allowSkinny={allowSkinny} allowSkinnySale={allowSkinnySale} />
    </ImageContainer>
  )
}

ImageBlock.propTypes = {
  pictures: arrayOf(shape()).isRequired,
  config: shape({
    imageSphere: shape().isRequired
  }).isRequired,
  target: shape({
    href: string.isRequired
  }),
  onTargetClick: func.isRequired,
  lazy: bool.isRequired,
  ynapParameter: string.isRequired,
  fetchPriority: string
}

export default ImageBlock
